// Access tokens live in memory only; the refresh token is an httpOnly cookie
// on this host managed by the BFF (/bff/*). Concurrent callers share one
// refresh request.

let token: string | null = null;
let expiresAt = 0;
let inflight: Promise<string> | null = null;

const bffHeaders = { "X-Requested-With": "sooquna-admin" };

export class SignedOut extends Error {}

export function loginURL(returnTo = location.pathname + location.search, opts: { signup?: boolean; stepUp?: boolean } = {}) {
  const q = new URLSearchParams({ return_to: returnTo });
  if (opts.signup) q.set("signup", "1");
  if (opts.stepUp) q.set("step_up", "1");
  return "/bff/login?" + q.toString();
}

// mfaSetupURL opens the account page to turn on two-step verification; once
// it's on, the page continues to the admin's step-up sign-in, which comes
// back to returnTo (the page the user was on).
export function mfaSetupURL(returnTo?: string) {
  const back = location.origin + loginURL(returnTo, { stepUp: true });
  return `${import.meta.env.VITE_ACCOUNTS_URL}/account?` + new URLSearchParams({ continue: back }).toString();
}

export function redirectToLogin(opts: { signup?: boolean; stepUp?: boolean; returnTo?: string } = {}): never {
  location.assign(loginURL(opts.returnTo, opts));
  throw new SignedOut();
}

// Transient: the api is restarting (dev hot reload, deploys) or a proxy hiccup.
export const transient = (status: number) => status === 502 || status === 503 || status === 504;

// withRetry retries network errors and transient statuses with backoff
// (0.5s, 1s, 2s, 4s, 4s: ~11.5s), then gives up with the last result.
export async function withRetry(send: () => Promise<Response>): Promise<Response> {
  let delay = 500;
  for (let attempt = 0; ; attempt++) {
    try {
      const res = await send();
      if (!transient(res.status) || attempt >= 5) return res;
    } catch (e) {
      if (attempt >= 5) throw e;
    }
    await new Promise((r) => setTimeout(r, delay));
    delay = Math.min(delay * 2, 4000);
  }
}

async function refresh(): Promise<string> {
  const res = await withRetry(() => fetch("/bff/token", { method: "POST", headers: bffHeaders, credentials: "same-origin" }));
  if (res.status === 401) throw new SignedOut();
  if (!res.ok) throw new Error(`token refresh failed (${res.status})`);
  const body = (await res.json()) as { access_token: string; expires_in: number };
  token = body.access_token;
  expiresAt = Date.now() + (body.expires_in - 30) * 1000;
  return token;
}

export async function accessToken(force = false): Promise<string> {
  if (!force && token && Date.now() < expiresAt) return token;
  inflight ??= refresh().finally(() => (inflight = null));
  return inflight;
}

export async function logout() {
  const res = await fetch("/bff/logout", { method: "POST", headers: bffHeaders, credentials: "same-origin" });
  token = null;
  const body = res.ok ? ((await res.json()) as { logout_url?: string }) : {};
  location.assign(body.logout_url ?? "/");
}
